import type { DesktopUpdateState } from '@dob/domain';
import { Download } from 'lucide-react';

import { useLang } from '../i18n.ts';
import { updateStepText } from '../pages/updates/updateText.ts';

/**
 * Dob Desktop (M7-7, O26 q7): a PC that missed its update window updates at start-up, before work
 * begins. Meanwhile this screen replaces every work screen: what is happening (sending the pending
 * operations, downloading, installing) and "do not switch off the PC". It ends by itself: the
 * program restarts on the new version, or opens for work if the update cannot be done now.
 */
export function UpdateBeforeWork({ state }: { readonly state: DesktopUpdateState }) {
  const { t, lang } = useLang();
  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-xl rounded-2xl border border-border bg-surface p-8 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <Download className="size-8 shrink-0 text-info" aria-hidden />
          <h1 className="text-2xl font-bold">{t('updates.beforeWorkTitle')}</h1>
        </div>
        <p className="text-sm text-muted">{t('updates.beforeWorkBody')}</p>
        <p
          className="mt-6 text-lg font-semibold"
          role="status"
          data-update-before-work={state.step}
        >
          {updateStepText(state, t, lang)}
        </p>
        {state.progress ? (
          <div
            className="mt-3 h-3 overflow-hidden rounded bg-surface-2"
            role="progressbar"
            aria-valuenow={state.progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full bg-info"
              style={{ width: `${String(state.progress.percent)}%` }}
            />
          </div>
        ) : null}
        <p className="mt-6 font-bold">{t('updates.beforeWorkDontClose')}</p>
      </div>
    </main>
  );
}
